import { AxiosError } from 'axios'
import { useCallback } from 'react'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { Container } from '@/interfaces/container'

import APIServicesV2 from '../../api/APIServicesV2'

export interface ContainerQueryParams {
  per_page?: number | null
  page?: number | null
  status?: string | null
  operationNumber?: string | null | number
  name?: string | null
  search?: string | null
  categoryId?: string | null
  isReturned?: string | null
  ownerCompanyId?: string | null
  managerCompanyId?: string | null
  transporterCompanyId?: string | null
  storageCompanyId?: string | null
}

export interface ContainerResponse {
  totalItems: number
  currentPage: number
  totalPages: number
  items: Container[]
}

// Create a unique, descriptive key for this query
const CONTAINER_QUERY_KEY = 'containers'

/**
 * Fetch containers from the API
 * Now includes search parameter support
 */
const fetchContainers = async (
  params?: ContainerQueryParams
): Promise<ContainerResponse> => {
  // Filter out null/undefined values to clean up the request
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => value != null)
  )

  const response = await APIServicesV2.get('/containers', {
    params: cleanParams,
  })
  return response.data
}

/**
 * Custom hook for fetching containers with filtering
 * This hook now properly uses external parameters without internal state conflicts
 *
 * @param params - Query parameters for filtering containers
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useContainerQuery = ({
  params = {},
  options,
  businessId,
}: {
  params?: ContainerQueryParams
  options?: Partial<UseQueryOptions<ContainerResponse, AxiosError>>
  businessId: number
}) => {
  // Normalize params with defaults
  const normalizedParams: ContainerQueryParams = {
    page: 1,
    per_page: 10,
    ...params,
  }

  const queryResult = useQuery<ContainerResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: [CONTAINER_QUERY_KEY, normalizedParams, businessId],

    queryFn: () => {
      return fetchContainers(normalizedParams)
    },

    // Configuración de cache optimizada para búsquedas (igual que inventory)
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,

    placeholderData: (previousData) => {
      if (previousData && normalizedParams.search !== undefined) {
        return previousData
      }
      return undefined
    },

    // Apply any custom options
    ...options,
  })

  // Helper functions for pagination metadata
  const getCurrentPage = useCallback(() => {
    return normalizedParams.page || 1
  }, [normalizedParams.page])

  const getPageSize = useCallback(() => {
    return normalizedParams.per_page || 10
  }, [normalizedParams.per_page])

  const hasNextPage = useCallback(() => {
    if (!queryResult.data) return false
    const { currentPage, totalPages } = queryResult.data
    return currentPage < totalPages
  }, [queryResult.data])

  const hasPreviousPage = useCallback(() => {
    const currentPage = getCurrentPage()
    return currentPage > 1
  }, [getCurrentPage])

  return {
    ...queryResult,
    // Add pagination helpers and metadata
    pagination: {
      currentPage: getCurrentPage(),
      pageSize: getPageSize(),
      hasNextPage: hasNextPage(),
      hasPreviousPage: hasPreviousPage(),
      totalPages: queryResult.data?.totalPages || 0,
      totalItems: queryResult.data?.totalItems || 0,
      params: normalizedParams,
    },
  }
}

/**
 * Helper function to get the container query key
 * Useful for cache invalidation or prefetching
 */
export const getContainersQueryKey = (params?: ContainerQueryParams) => [
  CONTAINER_QUERY_KEY,
  params,
]
